/* LPC product-page sections: recommendations rail, jump links, fit carousel, size-finder button.
   Vanilla, no dependencies. Safe to load more than once (every lpc-* section that needs it includes the tag). */
(function () {
  if (window.lpcPdpSections) return;
  window.lpcPdpSections = true;

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function behavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function isRtl(el) {
    return window.getComputedStyle(el).direction === 'rtl';
  }

  // Run fn at most once per animation frame.
  function frame(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        fn();
      });
    };
  }

  function watchSize(el, fn) {
    if ('ResizeObserver' in window) {
      new ResizeObserver(fn).observe(el);
    } else {
      window.addEventListener('resize', fn);
    }
  }

  function markReady(el) {
    if (el.hasAttribute('data-lpc-ready')) return false;
    el.setAttribute('data-lpc-ready', '');
    return true;
  }

  /* ---------- product rail ---------- */
  function initRail(rail) {
    if (!markReady(rail)) return;

    var track = rail.querySelector('[data-lpc-rail-track]');
    if (!track) return;
    var controls = rail.querySelector('[data-lpc-rail-controls]');
    var prev = rail.querySelector('[data-lpc-rail-prev]');
    var next = rail.querySelector('[data-lpc-rail-next]');
    var fallback = rail.querySelector('template[data-lpc-rail-fallback]');

    var sync = frame(function () {
      var max = track.scrollWidth - track.clientWidth;
      var pos = Math.abs(track.scrollLeft);
      if (controls) controls.hidden = max <= 2;
      if (prev) prev.disabled = pos <= 2;
      if (next) next.disabled = pos >= max - 2;
    });

    function step(direction) {
      var items = track.children;
      if (!items.length) return;
      var pitch = items.length > 1 ? Math.abs(items[1].offsetLeft - items[0].offsetLeft) : items[0].offsetWidth;
      if (!pitch) return;
      var count = Math.max(1, Math.floor(track.clientWidth / pitch));
      if (isRtl(track)) direction = -direction;
      track.scrollBy({ left: direction * pitch * count, behavior: behavior() });
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', sync, { passive: true });
    if ('MutationObserver' in window) new MutationObserver(sync).observe(track, { childList: true });
    watchSize(track, sync);

    function finish(hasItems) {
      rail.setAttribute('data-state', hasItems ? 'ready' : 'empty');
      sync();
    }

    function useFallback() {
      if (!fallback || !fallback.content || !fallback.content.children.length) return false;
      track.innerHTML = '';
      track.appendChild(fallback.content.cloneNode(true));
      track.scrollLeft = 0;
      return true;
    }

    function load() {
      var url = rail.getAttribute('data-url');
      if (!url) {
        finish(useFallback());
        return;
      }
      fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('lpc rail ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-lpc-rail-track]');
          if (fresh && fresh.children.length) {
            track.innerHTML = fresh.innerHTML;
            track.scrollLeft = 0;
            return true;
          }
          return false;
        })
        .catch(function () {
          return false;
        })
        .then(function (loaded) {
          finish(loaded || useFallback());
          if (loaded) track.dispatchEvent(new CustomEvent('lpc:cards-added', { bubbles: true }));
        });
    }

    // Load when the rail is near the viewport, like Dawn's product recommendations.
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(rail);
    } else {
      load();
    }
  }

  /* ---------- jump links: hide a link whose target is not on the page ---------- */
  function initJump(nav) {
    if (!markReady(nav)) return;
    each(nav.querySelectorAll('a[href^="#"]'), function (link) {
      var id = link.getAttribute('href').slice(1);
      try {
        id = decodeURIComponent(id);
      } catch (e) {}
      if (id && !document.getElementById(id)) link.hidden = true;
    });
  }

  /* ---------- fit carousel ---------- */
  function initFit(root) {
    if (!markReady(root)) return;

    var track = root.querySelector('[data-lpc-fit-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    var controls = root.querySelector('[data-lpc-fit-controls]');
    var prev = root.querySelector('[data-lpc-fit-prev]');
    var next = root.querySelector('[data-lpc-fit-next]');
    var pills = root.querySelectorAll('[data-lpc-fit-size]');

    if (!slides.length) return;
    if (controls) controls.hidden = slides.length < 2;

    function current() {
      var width = track.clientWidth || 1;
      return Math.max(0, Math.min(slides.length - 1, Math.round(Math.abs(track.scrollLeft) / width)));
    }

    function mark(index) {
      var size = slides[index].getAttribute('data-size') || '';
      each(pills, function (pill) {
        pill.setAttribute('aria-pressed', pill.getAttribute('data-size') === size ? 'true' : 'false');
      });
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
    }

    // While a button-driven scroll is running, ignore scroll events until it lands.
    var pending = null;
    var pendingTimer = 0;

    function base() {
      return pending === null ? current() : pending;
    }

    function go(index) {
      index = Math.max(0, Math.min(slides.length - 1, index));
      pending = index;
      window.clearTimeout(pendingTimer);
      pendingTimer = window.setTimeout(function () {
        pending = null;
        mark(current());
      }, 700);
      mark(index);
      track.scrollTo({ left: index * track.clientWidth * (isRtl(track) ? -1 : 1), behavior: behavior() });
    }

    if (prev) prev.addEventListener('click', function () { go(base() - 1); });
    if (next) next.addEventListener('click', function () { go(base() + 1); });
    each(pills, function (pill) {
      pill.addEventListener('click', function () {
        go(parseInt(pill.getAttribute('data-lpc-fit-size'), 10) || 0);
      });
    });
    track.addEventListener(
      'scroll',
      frame(function () {
        var index = current();
        if (pending !== null) {
          if (index !== pending) return;
          pending = null;
        }
        mark(index);
      }),
      { passive: true }
    );
    mark(0);
  }

  /* ---------- size finder button (the main product section listens for this event) ---------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-lpc-fit-open]') : null;
    if (!button) return;
    event.preventDefault();
    document.dispatchEvent(new CustomEvent('lpc:open-size-finder'));
  });

  function initAll() {
    each(document.querySelectorAll('[data-lpc-rail]'), initRail);
    each(document.querySelectorAll('[data-lpc-jump]'), initJump);
    each(document.querySelectorAll('[data-lpc-fit]'), initFit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
